import { useMemo } from "react";
import { bool, object } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { MinAvgMaxKeys } from "@repo/utils";
import {
  useEstimateStructure,
  useVisibilityModeCellClassName,
  useEstimateEditorSettings,
  ValueCell,
  ResetValueButton,
  SummaryWorkTypeBufferTooltip,
} from "@tools";

export const HoursCell = observer(({
  element,
  displayCellContent,
  isWorkType,
  ...props
}) => {
  const { t } = useTranslation();
  const { allowEdition, useProfitability } = useEstimateEditorSettings();
  const structure = useEstimateStructure();
  const className = useVisibilityModeCellClassName(structure.visibility.hideSummaryHours);
  
  const { displayAnyValue, usedBuffers, displayWorkTypeTwoValues } = structure;
  
  const {
    modifier,
    // showDecimals,
    useMinMax,
    usedRoundDecimals,
    maxValuesAsBuffer,
  } = structure.settings;
  
  const {
    displayHours,
    displayHoursMax,
    displayUnbufferedHours,
    displayUnbufferedHoursMax,
    hasFixedPrice,
    // inBreakdown,
    hasFixedHours,
    // usesSummaryPercent,
  } = element;
  
  // const messageProps = useBreakdownValueInputMessage(inBreakdown && isWorkType);
  
  const editable = allowEdition && !useProfitability;
  // const showResetButton = displayCellContent && allowEdition && !inBreakdown && hasCustomHours && usesSummaryPercent;
  const showResetButton = displayCellContent && allowEdition && hasFixedHours && !hasFixedPrice;// && usesSummaryPercent;
  
  const showMaxValue = useMemo(() => (
    displayWorkTypeTwoValues[element.workTypeId] && ((editable && !maxValuesAsBuffer) || displayHours !== displayHoursMax)
  ), [ displayWorkTypeTwoValues, editable, displayHours, displayHoursMax, maxValuesAsBuffer ]);
  
  const displayUsedBuffers = useMemo(() => (
    isWorkType && displayCellContent && allowEdition && !useProfitability && displayHours && structure.hasUsedWorkTypeBuffers(element.id)
  ), [displayCellContent, allowEdition, displayHours, useProfitability, isWorkType, usedBuffers]);
  
  const handleHoursChange = (hour, isMax) => {
    // structure.historyManager.startGroup();
    let key = MinAvgMaxKeys.AVG;
    if(isMax)
      key = MinAvgMaxKeys.MAX;
    else if(!maxValuesAsBuffer && useMinMax)
      key = MinAvgMaxKeys.MIN;

    const v = parseFloat((hour * modifier).toPrecision(12));

    element.setFixedHours({ [key]: v });

    if(v === element.unbufferedHours[key])
      return true;
    
    // handleRiskAlertOpen();
    // structure.historyManager.stopGroup();
  };
  
  const handleHoursReset = () => {
    element.resetFixedHours(true);
  };
  
  return (
    <ValueCell
      size="large"
      emptyInputs={hasFixedPrice || !displayAnyValue}
      value={displayHours}
      valueMax={displayHoursMax}
      placeholderMin={displayUnbufferedHours}
      placeholderMax={displayUnbufferedHoursMax}
      displayMax={showMaxValue}
      onValueChange={handleHoursChange}
      displayContent={((allowEdition && !useProfitability) || !hasFixedPrice) && displayCellContent}
      editable={{ min: editable, max: editable && !maxValuesAsBuffer && useMinMax }}
      showDecimals
      warning={showResetButton}
      useSeparator={useProfitability}
      decimals={usedRoundDecimals}
      className={className}
      textPresetClass="preset-summaryText"
      inputClassName="input-hours"
      inputMinTooltipMessage={
        displayUsedBuffers && !maxValuesAsBuffer &&
        <SummaryWorkTypeBufferTooltip
          workType={element}
          cleanValue={displayUnbufferedHours}
        />
      }
      inputMaxTooltipMessage={
        displayUsedBuffers &&
        <SummaryWorkTypeBufferTooltip
          workType={element}
          cleanValue={displayUnbufferedHoursMax}
          isMax
        />
      }
      //{ ...messageProps }
      { ...props }
    >
      {
        showResetButton &&
        <ResetValueButton action={handleHoursReset}>
          {t("views.editor.revert_value")}
        </ResetValueButton>
      }
    </ValueCell>
  );
});

HoursCell.propTypes = {
  element: object.isRequired,
  displayCellContent: bool,
  isWorkType: bool,
  extendPadding: bool,
};
